/**
 * Product Import Modal
 * Accepts a CSV file upload, parses it, shows a preview, and sends to the API.
 * CSV format: semicolon-separated (Brazilian Excel default).
 * Columns: barcode;name;sale_price;cost_price;unit;category;stock_quantity
 */
import { useState, useRef } from 'react';
import { Upload, FileText, X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';

interface ProductImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface ParsedProduct {
  barcode: string;
  name: string;
  sale_price: number;
  cost_price: number;
  unit: string;
  category: string;
  stock_quantity: number;
}

interface ImportResult {
  message: string;
  imported: number;
  skipped: number;
  errors: string[];
}

/**
 * Simple CSV parser using semicolons (Brazilian Excel default).
 */
function parseCSV(text: string): Record<string, string>[] {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];

  const headers = lines[0]!.split(';').map(h => h.trim().toLowerCase().replace(/"/g, ''));

  return lines.slice(1).map(line => {
    const values = line.split(';').map(v => v.trim().replace(/"/g, ''));
    const row: Record<string, string> = {};
    headers.forEach((header, i) => {
      row[header] = values[i] || '';
    });
    return row;
  });
}

function parsePriceValue(value: string): number {
  if (!value) return 0;
  // Handle Brazilian format: "1.234,56" -> 1234.56
  const cleaned = value.replace(/\./g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

export function ProductImportModal({ isOpen, onClose, onSuccess }: ProductImportModalProps) {
  const [parsedProducts, setParsedProducts] = useState<ParsedProduct[]>([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setResult(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const rows = parseCSV(text);

      const products: ParsedProduct[] = rows
        .filter(row => row.barcode || row.codigo || row.cod_barras)
        .map(row => ({
          barcode: row.barcode || row.codigo || row.cod_barras || '',
          name: row.name || row.nome || row.produto || row.descricao || '',
          sale_price: parsePriceValue(row.sale_price || row.preco_venda || row.preco || row.valor || '0'),
          cost_price: parsePriceValue(row.cost_price || row.preco_custo || row.custo || '0'),
          unit: row.unit || row.unidade || row.un || 'un',
          category: row.category || row.categoria || row.grupo || 'Geral',
          stock_quantity: parseInt(row.stock_quantity || row.estoque || row.quantidade || '0', 10) || 0,
        }));

      setParsedProducts(products);
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleImport = async () => {
    if (parsedProducts.length === 0) {
      toast.error('Nenhum produto para importar');
      return;
    }

    if (parsedProducts.length > 500) {
      toast.error('Máximo de 500 produtos por importação');
      return;
    }

    setImporting(true);
    try {
      const response = await apiFetch('/api/products/import', {
        method: 'POST',
        body: JSON.stringify({ products: parsedProducts }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Erro na importação' }));
        toast.error(err.error || 'Erro na importação');
        return;
      }

      const data: ImportResult = await response.json();
      setResult(data);
      toast.success(data.message);
      onSuccess?.();
    } catch {
      toast.error('Erro de conexão ao importar produtos');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setParsedProducts([]);
    setFileName('');
    setResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-3">
            <Upload className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Importar Produtos (CSV)</h2>
          </div>
          <button onClick={handleClose} className="rounded-lg p-1 hover:bg-muted transition-colors">
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-6" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          {/* Upload area */}
          {!result && (
            <div className="mb-6">
              <div
                onClick={() => fileInputRef.current?.click()}
                className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border p-8 transition-colors hover:border-primary hover:bg-primary/5"
              >
                <FileText className="mb-3 h-10 w-10 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">
                  {fileName || 'Clique para selecionar um arquivo CSV'}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Formato: barcode;name;sale_price;cost_price;unit;category;stock_quantity
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Separador: ponto e vírgula (;) • Aceita nomes em português
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          )}

          {/* Preview table */}
          {parsedProducts.length > 0 && !result && (
            <div className="mb-4">
              <p className="mb-3 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{parsedProducts.length}</span> produtos encontrados no arquivo
              </p>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Código</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Nome</th>
                      <th className="px-3 py-2 text-right font-medium text-muted-foreground">Preço Venda</th>
                      <th className="px-3 py-2 text-right font-medium text-muted-foreground">Preço Custo</th>
                      <th className="px-3 py-2 text-center font-medium text-muted-foreground">Unidade</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Categoria</th>
                      <th className="px-3 py-2 text-right font-medium text-muted-foreground">Estoque</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedProducts.slice(0, 20).map((product, i) => (
                      <tr key={i} className="border-b border-border/50">
                        <td className="px-3 py-2 font-mono text-xs text-foreground">{product.barcode}</td>
                        <td className="px-3 py-2 text-foreground">{product.name}</td>
                        <td className="px-3 py-2 text-right text-foreground">
                          {product.sale_price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </td>
                        <td className="px-3 py-2 text-right text-muted-foreground">
                          {product.cost_price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{product.unit}</td>
                        <td className="px-3 py-2 text-muted-foreground">{product.category}</td>
                        <td className="px-3 py-2 text-right text-muted-foreground">{product.stock_quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedProducts.length > 20 && (
                  <p className="px-3 py-2 text-xs text-muted-foreground text-center">
                    ... e mais {parsedProducts.length - 20} produtos
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 rounded-lg bg-green-500/10 p-4">
                <CheckCircle className="h-6 w-6 text-green-400" />
                <div>
                  <p className="font-medium text-foreground">{result.message}</p>
                  <p className="text-sm text-muted-foreground">
                    {result.imported} importados • {result.skipped} ignorados (duplicados ou inválidos)
                  </p>
                </div>
              </div>
              {result.errors.length > 0 && (
                <div className="rounded-lg bg-yellow-500/10 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-4 w-4 text-yellow-400" />
                    <p className="text-sm font-medium text-yellow-400">Avisos:</p>
                  </div>
                  <ul className="space-y-1">
                    {result.errors.map((err, i) => (
                      <li key={i} className="text-xs text-muted-foreground">• {err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
          <button
            onClick={handleClose}
            className="rounded-lg px-4 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors"
          >
            {result ? 'Fechar' : 'Cancelar'}
          </button>
          {!result && parsedProducts.length > 0 && (
            <button
              onClick={handleImport}
              disabled={importing}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Importar {parsedProducts.length} produtos
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
